import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { migrateGuestToNewUser } from "../../../utils/firestore/migrateGuestToNewUser";
import { recordLogin } from "../../../api/accountApi";
import * as Sentry from "@sentry/react";

/**
 * Hook to handle user login/session setup
 * Handles account creation, guest migration, context updates, and navigation
 * @param {Object} options - Configuration options
 * @param {Function} options.setUserId - Function to set userId in context
 * @param {Function} options.setEmail - Function to set email in context
 * @param {Function} options.setMoods - Function to clear moods in context
 * @param {Function} options.setCauses - Function to clear causes in context
 * @param {Function} options.refreshData - Function to refresh user data
 * @returns {Function} loginUser - Function to perform user login
 */
export const useUserLogin = ({
  setUserId,
  setEmail,
  setMoods,
  setCauses,
  refreshData,
}) => {
  const navigate = useNavigate();

  return useCallback(
    async (email, userUid, isExistingUser) => {
      // Creates the account if missing and stamps last_login_at - preserves
      // organizationId/isLead/defaultLanguage automatically either way, so
      // there's no need to fetch and re-merge the existing profile here.
      await recordLogin();

      if (!isExistingUser) {
        migrateGuestToNewUser(userUid);
      }

      // Set user in context and localStorage
      localStorage.setItem("tujud_uid", userUid);
      localStorage.setItem("emailForSignIn", email);
      setUserId(userUid);
      setEmail(email);

      Sentry.setUser({ id: userUid, userId: userUid });

      try {
        await refreshData(userUid, "all");
      } catch (error) {
        console.warn("Failed to refresh data after login:", error);
      }

      setTimeout(() => {
        navigate("/app/home", { replace: true });
      }, 1000);
    },
    [navigate, setUserId, setEmail, setMoods, setCauses, refreshData]
  );
};
